import React, { useMemo } from 'react';
import { useFinanceDashboradDataQuery } from '../../../store/slices/apiSlice';
import { Box } from '@mui/material';
import AnimatedBloqcubeLogo from '../../../components/Logo/AnimatedBloqcubeLogo';
import { Bar } from 'react-chartjs-2';

// Invoice Status Enum
export const InvoiceStatus = {
  PENDING: "pending",
  APPROVED: "approved",
  DECLINED: "declined",
  AMENDED: "amended",
//   PENDING_QC: "pendingQc",
};

// Status color mapping
const STATUS_COLORS = {
  [InvoiceStatus.PENDING]: {
    borderColor: '#FFB300',
    backgroundColor: '#FFF8E1',
    label: 'Pending'
  },
  [InvoiceStatus.APPROVED]: {
    borderColor: '#4CAF50',
    backgroundColor: '#E8F5E9',
    label: 'Approved'
  },
  [InvoiceStatus.DECLINED]: {
    borderColor: '#F44336',
    backgroundColor: '#FFEBEE',
    label: 'Declined'
  },
  [InvoiceStatus.AMENDED]: {
    borderColor: '#9C27B0',
    backgroundColor: '#F3E5F5',
    label: 'Amended'
  },
//   [InvoiceStatus.PENDING_QC]: {
//     borderColor: '#FF9800',
//     backgroundColor: '#FFF3E0',
//     label: 'Pending QC'
//   },
};

const InvoiceCounts = ({ clinicalStudyId, studySiteIds, siteList, ...props }) => {
  const { data: dashboardData, isLoading } = useFinanceDashboradDataQuery({
    data: { preference: props.preference, clinicalStudyId, studySiteIds: studySiteIds?.join(',') },
  });

  const { labels, statusData, fullLabels } = useMemo(() => {
    const labels = [];
    const fullLabels = [];
    const statusData = {};

    // Initialize status data arrays
    Object.keys(InvoiceStatus).forEach(status => {
      statusData[InvoiceStatus[status]] = [];
    });

    if (dashboardData) {
      const dashboardSites = dashboardData.bySite;

      for (let id in siteList) {
        const site = siteList[id];
        const currentSite = dashboardSites[site.id];

        if (!currentSite) continue;

        const siteName = site?.siteName || '';
        fullLabels.push(siteName);

        // Truncate long labels to 20 characters
        const truncatedLabel = siteName.length > 20 
          ? `${siteName.substring(0, 17)}...` 
          : siteName;

        labels.push(truncatedLabel);

        // Push data for each status
        Object.keys(InvoiceStatus).forEach(statusKey => {
          const status = InvoiceStatus[statusKey];
          const count = currentSite[status.toLowerCase()] || 0;
          statusData[status].push(count);
        });
      }
    }

    return {
      labels,
      fullLabels,
      statusData,
    };
  }, [dashboardData, studySiteIds, siteList]);

  // Check if all values are zero
  const allValuesZero = useMemo(() => {
    const allValues = Object.values(statusData).flat();
    return allValues.length > 0 && allValues.every(value => value === 0);
  }, [statusData]);

  // Create datasets only for statuses that have data or should always be shown
  const datasets = useMemo(() => {
    return Object.keys(InvoiceStatus).map(statusKey => {
      const status = InvoiceStatus[statusKey];
      const data = statusData[status] || [];
      const config = STATUS_COLORS[status];

      return {
        label: config.label,
        data: data,
        borderColor: config.borderColor,
        backgroundColor: config.backgroundColor,
        borderWidth: 2,
      };
    })
  }, [statusData, allValuesZero]);

  const options = {
    indexAxis: 'y',
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          usePointStyle: true,
          font: {
            size: 11,
          },
        },
      },
      datalabels: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function (context) {
            const dataIndex = context[0].dataIndex;
            return fullLabels[dataIndex] || context[0].label;
          },
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.x;
            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        min: 0,
        max: allValuesZero ? 10 : undefined,
        ticks: {
          stepSize: allValuesZero ? 2 : undefined,
          callback: function (value) {
            return Number.isInteger(value) ? value : '';
          },
        },
        title: {
          display: true,
          text: 'Number of Invoices',
          font: {
            size: 12,
          },
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          maxTicksLimit: 10,
          callback: function (value, index) {
            const label = this.getLabelForValue(value);
            return label;
          },
        },
        afterFit: function (scale) {
          scale.width = Math.min(scale.width, 150);
        },
        title: {
          display: true,
          text: 'Sites',
          font: {
            size: 12,
          },
        },
      },
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10,
      },
    },
  };

  const data = {
    labels: labels,
    datasets: datasets,
  };

  return (
    <Box
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}
    >
      {isLoading && (
        <Box sx={{ 
          height: '100%', 
          width: '100%', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center' 
        }}>
          <Box sx={{ height: 250, width: 250 }}>
            <AnimatedBloqcubeLogo />
          </Box>
        </Box>
      )}
      {dashboardData && <Bar options={options} data={data} />}
    </Box>
  );
};

export default InvoiceCounts;